import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";

import { applyDensity } from "./applyDensity";
import { readDensityPreference, writeDensityPreference } from "./storage";
import type { DensityPreference } from "./types";

type DensityContextValue = {
  density: DensityPreference;
  setDensity: (density: DensityPreference) => void;
};

const DensityContext = createContext<DensityContextValue | null>(null);

type Props = {
  children: ReactNode;
  /** Test hook: overrides the value read from localStorage. */
  initialDensity?: DensityPreference;
};

export function DensityProvider({ children, initialDensity }: Props) {
  const [density, setDensityState] = useState<DensityPreference>(
    () => initialDensity ?? readDensityPreference()
  );

  useEffect(() => {
    applyDensity(density);
  }, [density]);

  const setDensity = useCallback((next: DensityPreference) => {
    setDensityState(next);
    writeDensityPreference(next);
  }, []);

  const value = useMemo<DensityContextValue>(
    () => ({ density, setDensity }),
    [density, setDensity]
  );

  return (
    <DensityContext.Provider value={value}>{children}</DensityContext.Provider>
  );
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error("useDensity must be used within <DensityProvider>");
  return ctx;
}

/** For leaf components that may render without the provider (tests). Returns `null` there. */
export function useOptionalDensity(): DensityContextValue | null {
  return useContext(DensityContext);
}
